import {
  AlertTriangle,
  Calendar,
  CheckCircle2,
  Clock,
  History,
  Info,
  Loader2,
  RefreshCw,
  User,
  Zap,
} from "lucide-react";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { ApiError } from "@/api/client";
import type {
  ForecastRunResponseApi,
  ForecastRunStatusResponseApi,
} from "@/api/models/forecast";
import {
  getForecastRunHistory,
  getForecastRunStatus,
  triggerForecastRun,
} from "@/api/services/forecastService";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useApp } from "@/contexts/AppContext";
import { useForecastRefresh } from "@/contexts/ForecastRefreshContext";
import { useUsers } from "@/contexts/UsersContext";
import { useFormatting } from "@/hooks/useFormatting";
import { usePermissions } from "@/hooks/usePermissions";
import { useTerminology } from "@/hooks/useTerminology";

// ─── Demo seed data ────────────────────────────────────────────────────────────

interface RefreshHistoryEntry {
  id: string;
  dateTime: Date;
  type: "Scheduled" | "Manual";
  triggeredBy: string;
  durationMs: number;
  notes?: string;
}

function buildSeedHistory(): RefreshHistoryEntry[] {
  const now = new Date();
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);

  const d = (daysAgo: number, hour: number, minute = 0): Date => {
    const dt = new Date(startOfToday);
    dt.setDate(dt.getDate() - daysAgo);
    dt.setHours(hour, minute, 0, 0);
    return dt;
  };

  return [
    {
      id: "rh-1",
      dateTime: d(0, 6, 0),
      type: "Scheduled",
      triggeredBy: "System",
      durationMs: 222_000, // 3 min 42 sec
      notes:
        "✓ Forecast completed successfully — 3 data sources contributed data",
    },
    {
      id: "rh-2",
      dateTime: d(1, 14, 23),
      type: "Manual",
      triggeredBy: "Sarah Mitchell",
      durationMs: 247_000, // 4 min 7 sec
      notes:
        "⚠ 1 data source returned incomplete data — forecast recalculated with available data",
    },
    {
      id: "rh-3",
      dateTime: d(2, 6, 0),
      type: "Scheduled",
      triggeredBy: "System",
      durationMs: 218_000, // 3 min 38 sec
      notes:
        "✓ Forecast completed successfully — 3 data sources contributed data",
    },
    {
      id: "rh-4",
      dateTime: d(3, 6, 0),
      type: "Scheduled",
      triggeredBy: "System",
      durationMs: 231_000, // 3 min 51 sec
      notes:
        "✓ Forecast completed successfully — 3 data sources contributed data",
    },
  ];
}

const SEED_HISTORY = buildSeedHistory();

// The last scheduled refresh is SEED_HISTORY[0]
const LAST_REFRESH = SEED_HISTORY[0];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function startOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

// Format a Date as a local yyyy-MM-dd string (avoids UTC offset shifting the day).
function toISODate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDuration(ms: number): string {
  const totalSec = Math.round(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min === 0) return `${sec} sec`;
  return `${min} min ${sec} sec`;
}

function timeAgoLabel(date: Date): string {
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
  if (diffHours > 0)
    return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
  if (diffMin > 0) return `${diffMin} minute${diffMin !== 1 ? "s" : ""} ago`;
  return "just now";
}

function formatDateTime(date: Date): string {
  const today = new Date();
  const isToday =
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();

  const timeStr = date.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });

  if (isToday) return `Today at ${timeStr}`;

  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const isYesterday =
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear();

  if (isYesterday) return `Yesterday at ${timeStr}`;

  return `${date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} at ${timeStr}`;
}

function mapRunsToHistory(
  runs: ForecastRunResponseApi[],
): RefreshHistoryEntry[] {
  return runs.map((r) => ({
    id: String(r.id),
    dateTime: r.startedAt ? new Date(r.startedAt) : new Date(),
    type:
      r.triggerType === "scheduled"
        ? ("Scheduled" as const)
        : ("Manual" as const),
    triggeredBy: r.triggeredByUserId ?? "System",
    durationMs: r.durationMs ?? 0,
    notes: r.notes ?? undefined,
  }));
}

// ─── Component ────────────────────────────────────────────────────────────────

export const ForecastRefreshPage: React.FC = () => {
  const { currentUser, mode } = useApp();
  const { hasPermission } = usePermissions();
  const { getForecastTerm } = useTerminology();
  const {
    refreshState,
    startRefresh,
    setTriggeredRunId,
    completeRefresh,
    resetRefresh,
  } = useForecastRefresh();
  const { getUserById } = useUsers();

  const resolveUserName = (userId: string | null): string => {
    if (!userId || userId === "System") return "System";
    const user = getUserById(userId);
    return user?.name ?? userId;
  };

  if (!hasPermission("trigger_forecast_refresh")) return null;
  const { formatCurrency: _fc } = useFormatting();

  const [acknowledged, setAcknowledged] = useState(false);
  const [forecastStartDate, setForecastStartDate] =
    useState<Date>(startOfToday);
  const [history, setHistory] = useState<RefreshHistoryEntry[]>(
    mode === "demo" ? SEED_HISTORY : [],
  );
  const [liveStatus, setLiveStatus] =
    useState<ForecastRunStatusResponseApi | null>(null);
  const [isLoading, setIsLoading] = useState(mode === "live");

  const simulationTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (simulationTimer.current) {
        clearTimeout(simulationTimer.current);
      }
    };
  }, []);

  // ─── Live mode: fetch current status + history on mount ───────────────────
  useEffect(() => {
    if (mode !== "live") return;
    let cancelled = false;

    const fetchLiveData = async () => {
      try {
        const [statusRes, historyRes] = await Promise.all([
          getForecastRunStatus(),
          getForecastRunHistory(),
        ]);
        if (cancelled) return;
        setLiveStatus(statusRes);
        setHistory(mapRunsToHistory(historyRes));
      } catch {
        // Silently ignore — user sees empty state
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchLiveData();
    return () => {
      cancelled = true;
    };
  }, [mode]);

  // ─── Live mode: re-fetch history after a refresh completes ────────────────
  useEffect(() => {
    if (mode !== "live" || refreshState.status !== "complete") return;
    let cancelled = false;

    const refetch = async () => {
      try {
        const [statusRes, historyRes] = await Promise.all([
          getForecastRunStatus(),
          getForecastRunHistory(),
        ]);
        if (cancelled) return;
        setLiveStatus(statusRes);
        setHistory(mapRunsToHistory(historyRes));
      } catch {
        // Silently ignore
      }
    };

    refetch();
    return () => {
      cancelled = true;
    };
  }, [mode, refreshState.status]);

  const isInProgress = refreshState.status === "in-progress";
  const isComplete = refreshState.status === "complete";

  // Estimated duration: live mode uses the backend average of prior completed
  // runs. On a first run there is no history, so the backend returns no
  // estimate and we show none (never fall back to demo seed data in live mode).
  // Demo mode uses seed + random variance.
  const estimatedDurationMs =
    mode === "live"
      ? (liveStatus?.estimatedDurationMs ?? 0)
      : LAST_REFRESH.durationMs + (Math.random() * 20_000 - 10_000);
  const hasEstimate = estimatedDurationMs > 0;

  // Derive current status fields from live data or seed data
  const lastRefreshDate =
    mode === "live" && liveStatus?.startedAt
      ? new Date(liveStatus.startedAt)
      : LAST_REFRESH.dateTime;
  const lastRefreshDurationMs =
    mode === "live" && liveStatus?.completedAt && liveStatus?.startedAt
      ? new Date(liveStatus.completedAt).getTime() -
        new Date(liveStatus.startedAt).getTime()
      : LAST_REFRESH.durationMs;
  const lastRefreshTriggeredBy =
    mode === "live"
      ? resolveUserName(liveStatus?.triggeredBy ?? null)
      : LAST_REFRESH.triggeredBy;

  const hasNeverRun = mode === "live" && !liveStatus?.startedAt;

  const handleStartRefresh = async () => {
    if (!acknowledged || isInProgress) return;

    if (mode === "live") {
      startRefresh(currentUser.name, estimatedDurationMs);
      try {
        const runId = await triggerForecastRun(toISODate(forecastStartDate));
        setTriggeredRunId(runId);
      } catch (err) {
        resetRefresh();
        if (err instanceof ApiError && err.status === 409) {
          console.warn(
            "Forecast run rejected — another run is already active for this tenant.",
          );
        } else if (err instanceof ApiError && err.status === 503) {
          console.warn(
            "Forecast run could not be queued for processing — please try again.",
          );
        }
      }
      return;
    }

    // Demo mode: simulate completion after estimated duration
    if (simulationTimer.current) {
      clearTimeout(simulationTimer.current);
    }

    startRefresh(currentUser.name, estimatedDurationMs);

    simulationTimer.current = setTimeout(() => {
      completeRefresh();
      const newEntry: RefreshHistoryEntry = {
        id: `rh-manual-${Date.now()}`,
        dateTime: new Date(),
        type: "Manual",
        triggeredBy: currentUser.name,
        durationMs: Math.round(estimatedDurationMs),
      };
      setHistory((prev) => [newEntry, ...prev]);
      simulationTimer.current = null;
    }, estimatedDurationMs);
  };

  // Progress calculation (0–100%)
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (
      !isInProgress ||
      !refreshState.startTime ||
      refreshState.estimatedDurationMs <= 0
    ) {
      setProgress(0);
      return;
    }
    const interval = setInterval(() => {
      const elapsed = Date.now() - refreshState.startTime!.getTime();
      const pct = Math.min(
        95,
        (elapsed / refreshState.estimatedDurationMs) * 100,
      );
      setProgress(Math.round(pct));
    }, 500);
    return () => clearInterval(interval);
  }, [isInProgress, refreshState.startTime, refreshState.estimatedDurationMs]);

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* In-progress / complete status alert */}
      {isInProgress && (
        <Alert variant="warning">
          <AlertDescription className="text-amber-800 font-medium">
            {getForecastTerm(true)} refresh in progress
            {hasEstimate ? ` — ${progress}% complete` : ""}. A banner is
            displayed to all users. All areas remain usable —{" "}
            {getForecastTerm(false)} data will update once complete.
          </AlertDescription>
        </Alert>
      )}
      {isComplete && (
        <Alert variant="success">
          <AlertDescription className="text-green-800 font-medium">
            {getForecastTerm(true)} refresh complete. New{" "}
            {getForecastTerm(false)} is now live.
          </AlertDescription>
        </Alert>
      )}

      {/* Zone 1 — Current Status */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Zap className="h-4 w-4 text-teal-600" />
            Current Status
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-x-8 gap-y-4 text-sm">
          {isLoading ? (
            <div className="col-span-2 flex items-center gap-2 text-gray-400 py-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading status…
            </div>
          ) : hasNeverRun ? (
            <p className="col-span-2 text-sm text-gray-500 py-2">
              No {getForecastTerm(false)} has run yet. Trigger a refresh below
              to generate your first {getForecastTerm(false)}.
            </p>
          ) : (
            <>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide mb-0.5">
                  Last refresh
                </p>
                <p className="font-medium text-gray-900">
                  {formatDateTime(lastRefreshDate)}
                  <span className="text-gray-400 font-normal ml-1">
                    · {timeAgoLabel(lastRefreshDate)}
                  </span>
                </p>
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide mb-0.5">
                  Duration
                </p>
                <p className="font-medium text-gray-900">
                  {formatDuration(lastRefreshDurationMs)}
                </p>
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide mb-0.5">
                  Triggered by
                </p>
                <p className="font-medium text-gray-900">
                  {lastRefreshTriggeredBy}
                </p>
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide mb-0.5">
                  {getForecastTerm(true)} age
                </p>
                <p className="font-medium text-gray-900">
                  {timeAgoLabel(lastRefreshDate)} old
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Zone 2 — What to Expect */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Info className="h-4 w-4 text-teal-600" />
            What to Expect
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-gray-700">
          {!hasNeverRun && (
            <>
              <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                <div>
                  <p className="text-gray-500 text-xs uppercase tracking-wide mb-0.5">
                    Estimated duration
                  </p>
                  <p className="font-medium text-gray-900">
                    {hasEstimate
                      ? `${formatDuration(estimatedDurationMs)} (based on last refresh)`
                      : "Available after your first completed refresh"}
                  </p>
                </div>
              </div>

              <Separator />
            </>
          )}

          <div className="space-y-2">
            <p className="font-medium text-gray-900">
              What happens during a refresh
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li>
                The base {getForecastTerm(false)} (90-day and 12-month)
                recalculates from all connected data sources.
              </li>
              <li>
                Updated metrics, data points, and contributing source
                information are generated for each {getForecastTerm(false)}{" "}
                period.
              </li>
              <li>
                Scenario impact metrics are recalculated against the updated
                base {getForecastTerm(false)}.
              </li>
            </ul>
          </div>

          <div className="space-y-2">
            <p className="font-medium text-gray-900">
              What happens during a refresh
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li>
                A banner will appear across the application informing all users
                that a refresh is in progress.
              </li>
              <li>All areas remain fully usable during a refresh.</li>
              <li>
                {getForecastTerm(true)} data may be temporarily stale until the
                refresh completes.
              </li>
              <li>
                The banner disappears automatically when the refresh finishes.
              </li>
            </ul>
          </div>

          <div className="flex gap-3 rounded-lg bg-blue-50 p-4 text-sm text-blue-900 border border-blue-100">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 text-blue-600 mt-0.5" />
            <p>
              If you find yourself triggering manual refreshes regularly,
              consider adjusting your scheduled refresh frequency in{" "}
              <span className="font-medium">
                System Settings &gt; General Settings
              </span>
              .
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Trigger section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <RefreshCw className="h-4 w-4 text-teal-600" />
            Start Refresh
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-3">
            <Checkbox
              id="acknowledge"
              checked={acknowledged}
              onCheckedChange={(v) => setAcknowledged(v === true)}
              disabled={isInProgress}
            />
            <Label
              htmlFor="acknowledge"
              className="text-sm text-gray-700 cursor-pointer leading-snug"
            >
              I understand this will recalculate all {getForecastTerm(false)}{" "}
              data. Values may be temporarily stale until the refresh completes.
            </Label>
          </div>

          <div className="flex flex-wrap items-end gap-4">
            <Button
              onClick={handleStartRefresh}
              disabled={!acknowledged || isInProgress}
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              {isInProgress ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Refresh in progress{hasEstimate ? ` (${progress}%)` : ""}…
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Start {getForecastTerm(true)} Refresh
                </>
              )}
            </Button>

            <div className="flex flex-col gap-1.5">
              <Label
                htmlFor="forecast-start-date"
                className="text-xs uppercase tracking-wide text-gray-500"
              >
                {getForecastTerm(true)} Start Date
              </Label>
              <DatePicker
                id="forecast-start-date"
                value={forecastStartDate}
                disabled={isInProgress}
                dateFormat="d MMM yyyy"
                className="w-48"
                onChange={(date) => {
                  if (date) {
                    const normalised = new Date(date);
                    normalised.setHours(0, 0, 0, 0);
                    setForecastStartDate(normalised);
                  }
                }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Zone 3 — Refresh History */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <History className="h-4 w-4 text-teal-600" />
            Refresh History
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date / Time</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Triggered By</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(() => {
                const sortedHistory = [...history]
                  .sort((a, b) => b.dateTime.getTime() - a.dateTime.getTime())
                  .slice(0, 20);
                return sortedHistory.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="text-center text-muted-foreground py-8"
                    >
                      No refresh history yet
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedHistory.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="whitespace-nowrap text-sm">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                          {formatDateTime(entry.dateTime)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            entry.type === "Scheduled" ? "secondary" : "outline"
                          }
                          className="text-xs"
                        >
                          {entry.type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        <div className="flex items-center gap-1.5">
                          <User className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                          {resolveUserName(entry.triggeredBy)}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        <div className="flex items-center gap-1.5">
                          <Clock className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                          {formatDuration(entry.durationMs)}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-gray-500 max-w-[300px]">
                        {entry.notes ? (
                          <Popover>
                            <PopoverTrigger asChild>
                              <span className="flex items-start gap-1.5 cursor-pointer truncate">
                                {entry.notes.startsWith("✓") ? (
                                  <CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                                ) : (
                                  <AlertTriangle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                                )}
                                <span className="truncate">
                                  {entry.notes.replace(/^[✓⚠]\s*/, "")}
                                </span>
                              </span>
                            </PopoverTrigger>
                            <PopoverContent className="w-96 text-sm">
                              <div className="flex items-start gap-2">
                                {entry.notes.startsWith("✓") ? (
                                  <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                                ) : (
                                  <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                                )}
                                <p className="text-gray-700">
                                  {entry.notes.replace(/^[✓⚠]\s*/, "")}
                                </p>
                              </div>
                            </PopoverContent>
                          </Popover>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                );
              })()}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
